// Create a new file: src/utils/phoneUtils.js
import { Country } from "country-state-city";

// Canadian area codes (NPA codes assigned to Canada under the +1 NANP)
const canadianAreaCodes = new Set([
    "204", "226", "236", "249", "250", "263", "289",
    "306", "343", "354", "365", "367", "382", "387",
    "403", "416", "418", "428", "431", "437", "438", "450", "460", "468", "474",
    "506", "514", "519", "548", "579", "581", "584", "587",
    "600", "604", "613", "639", "647", "672", "683",
    "705", "709", "742", "753", "778", "780", "782",
    "807", "819", "825", "867", "873", "879",
    "902", "905"
]);

/**
 * Detect whether a +1 phone number is United States or Canada
 * based on the 3-digit area code (NPA) after +1.
 * @param {string} digitsAfterCountryCode - The 10 digits after +1
 * @returns {string} "Canada" or "United States"
 */
const detectUSorCanada = (digitsAfterCountryCode) => {
    const areaCode = digitsAfterCountryCode.substring(0, 3);
    return canadianAreaCodes.has(areaCode) ? "Canada" : "United States";
};

// Country code to country name mapping
const countryCodeMap = {
    "+1": ["United States", "Canada"],
    "+7": ["Russia", "Kazakhstan"],
    "+20": ["Egypt"],
    "+27": ["South Africa"],
    "+30": ["Greece"],
    "+31": ["Netherlands"],
    "+32": ["Belgium"],
    "+33": ["France"],
    "+34": ["Spain"],
    "+36": ["Hungary"],
    "+39": ["Italy"],
    "+40": ["Romania"],
    "+41": ["Switzerland"],
    "+43": ["Austria"],
    "+44": ["United Kingdom"],
    "+45": ["Denmark"],
    "+46": ["Sweden"],
    "+47": ["Norway"],
    "+48": ["Poland"],
    "+49": ["Germany"],
    "+51": ["Peru"],
    "+52": ["Mexico"],
    "+53": ["Cuba"],
    "+54": ["Argentina"],
    "+55": ["Brazil"],
    "+56": ["Chile"],
    "+57": ["Colombia"],
    "+58": ["Venezuela"],
    "+60": ["Malaysia"],
    "+61": ["Australia"],
    "+62": ["Indonesia"],
    "+63": ["Philippines"],
    "+64": ["New Zealand"],
    "+65": ["Singapore"],
    "+66": ["Thailand"],
    "+81": ["Japan"],
    "+82": ["South Korea"],
    "+84": ["Vietnam"],
    "+86": ["China"],
    "+90": ["Turkey"],
    "+91": ["India"],
    "+92": ["Pakistan"],
    "+93": ["Afghanistan"],
    "+94": ["Sri Lanka"],
    "+95": ["Myanmar"],
    "+98": ["Iran"],
    "+212": ["Morocco"],
    "+213": ["Algeria"],
    "+216": ["Tunisia"],
    "+218": ["Libya"],
    "+220": ["Gambia"],
    "+221": ["Senegal"],
    "+222": ["Mauritania"],
    "+223": ["Mali"],
    "+224": ["Guinea"],
    "+225": ["Ivory Coast"],
    "+226": ["Burkina Faso"],
    "+227": ["Niger"],
    "+228": ["Togo"],
    "+229": ["Benin"],
    "+230": ["Mauritius"],
    "+231": ["Liberia"],
    "+232": ["Sierra Leone"],
    "+233": ["Ghana"],
    "+234": ["Nigeria"],
    "+235": ["Chad"],
    "+236": ["Central African Republic"],
    "+237": ["Cameroon"],
    "+238": ["Cape Verde"],
    "+239": ["Sao Tome and Principe"],
    "+240": ["Equatorial Guinea"],
    "+241": ["Gabon"],
    "+242": ["Republic of the Congo"],
    "+243": ["Democratic Republic of the Congo"],
    "+244": ["Angola"],
    "+245": ["Guinea-Bissau"],
    "+246": ["British Indian Ocean Territory"],
    "+248": ["Seychelles"],
    "+249": ["Sudan"],
    "+250": ["Rwanda"],
    "+251": ["Ethiopia"],
    "+252": ["Somalia"],
    "+253": ["Djibouti"],
    "+254": ["Kenya"],
    "+255": ["Tanzania"],
    "+256": ["Uganda"],
    "+257": ["Burundi"],
    "+258": ["Mozambique"],
    "+260": ["Zambia"],
    "+261": ["Madagascar"],
    "+262": ["Reunion"],
    "+263": ["Zimbabwe"],
    "+264": ["Namibia"],
    "+265": ["Malawi"],
    "+266": ["Lesotho"],
    "+267": ["Botswana"],
    "+268": ["Swaziland"],
    "+269": ["Comoros"],
    "+290": ["Saint Helena"],
    "+291": ["Eritrea"],
    "+297": ["Aruba"],
    "+298": ["Faroe Islands"],
    "+299": ["Greenland"],
    "+350": ["Gibraltar"],
    "+351": ["Portugal"],
    "+352": ["Luxembourg"],
    "+353": ["Ireland"],
    "+354": ["Iceland"],
    "+355": ["Albania"],
    "+356": ["Malta"],
    "+357": ["Cyprus"],
    "+358": ["Finland"],
    "+359": ["Bulgaria"],
    "+370": ["Lithuania"],
    "+371": ["Latvia"],
    "+372": ["Estonia"],
    "+373": ["Moldova"],
    "+374": ["Armenia"],
    "+375": ["Belarus"],
    "+376": ["Andorra"],
    "+377": ["Monaco"],
    "+378": ["San Marino"],
    "+380": ["Ukraine"],
    "+381": ["Serbia"],
    "+382": ["Montenegro"],
    "+383": ["Kosovo"],
    "+385": ["Croatia"],
    "+386": ["Slovenia"],
    "+387": ["Bosnia and Herzegovina"],
    "+389": ["North Macedonia"],
    "+420": ["Czech Republic"],
    "+421": ["Slovakia"],
    "+423": ["Liechtenstein"],
    "+500": ["Falkland Islands"],
    "+501": ["Belize"],
    "+502": ["Guatemala"],
    "+503": ["El Salvador"],
    "+504": ["Honduras"],
    "+505": ["Nicaragua"],
    "+506": ["Costa Rica"],
    "+507": ["Panama"],
    "+508": ["Saint Pierre and Miquelon"],
    "+509": ["Haiti"],
    "+590": ["Guadeloupe"],
    "+591": ["Bolivia"],
    "+592": ["Guyana"],
    "+593": ["Ecuador"],
    "+594": ["French Guiana"],
    "+595": ["Paraguay"],
    "+596": ["Martinique"],
    "+597": ["Suriname"],
    "+598": ["Uruguay"],
    "+599": ["Netherlands Antilles"],
    "+670": ["East Timor"],
    "+672": ["Australian External Territories"],
    "+673": ["Brunei"],
    "+674": ["Nauru"],
    "+675": ["Papua New Guinea"],
    "+676": ["Tonga"],
    "+677": ["Solomon Islands"],
    "+678": ["Vanuatu"],
    "+679": ["Fiji"],
    "+680": ["Palau"],
    "+681": ["Wallis and Futuna"],
    "+682": ["Cook Islands"],
    "+683": ["Niue"],
    "+684": ["American Samoa"],
    "+685": ["Samoa"],
    "+686": ["Kiribati"],
    "+687": ["New Caledonia"],
    "+688": ["Tuvalu"],
    "+689": ["French Polynesia"],
    "+690": ["Tokelau"],
    "+691": ["Micronesia"],
    "+692": ["Marshall Islands"],
    "+850": ["North Korea"],
    "+852": ["Hong Kong"],
    "+853": ["Macau"],
    "+855": ["Cambodia"],
    "+856": ["Laos"],
    "+880": ["Bangladesh"],
    "+886": ["Taiwan"],
    "+960": ["Maldives"],
    "+961": ["Lebanon"],
    "+962": ["Jordan"],
    "+963": ["Syria"],
    "+964": ["Iraq"],
    "+965": ["Kuwait"],
    "+966": ["Saudi Arabia"],
    "+967": ["Yemen"],
    "+968": ["Oman"],
    "+970": ["Palestine"],
    "+971": ["United Arab Emirates"],
    "+972": ["Israel"],
    "+973": ["Bahrain"],
    "+974": ["Qatar"],
    "+975": ["Bhutan"],
    "+976": ["Mongolia"],
    "+977": ["Nepal"],
    "+992": ["Tajikistan"],
    "+993": ["Turkmenistan"],
    "+994": ["Azerbaijan"],
    "+996": ["Kyrgyzstan"],
    "+998": ["Uzbekistan"]
};

/**
 * Convert Excel scientific notation back to full number string
 * Handles cases like "4.47E11" -> "447000000000"
 * WARNING: Trailing zeros may be lost due to Excel's conversion
 */
export const convertScientificNotation = (value) => {
    if (!value || typeof value !== "string") {
        return value;
    }
    
    // Check if value is in scientific notation (contains E or e followed by digits)
    const scientificNotationRegex = /^(\d+\.?\d*)[Ee]([+-]?\d+)$/;
    const match = value.match(scientificNotationRegex);
    
    if (match) {
        const base = parseFloat(match[1]);
        const exponent = parseInt(match[2]);
        const result = base * Math.pow(10, exponent);
        
        // Convert to string without scientific notation
        // Use toFixed to avoid scientific notation in the result
        // But we need to be careful - we might lose precision
        const resultString = result.toFixed(0);
        
        return resultString;
    }
    
    return value;
};

export const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber || typeof phoneNumber !== "string") {
        return {
            isValid: false,
            error: "Phone number is required",
            countryCode: null,
            detectedCountry: null
        };
    }

    // Convert Excel scientific notation first (e.g., "4.47E11" -> "447000000000")
    phoneNumber = convertScientificNotation(phoneNumber);

    // Remove all non-digit characters except +
    let cleanPhone = phoneNumber.replace(/[^+\d]/g, "");

    // If phone doesn't start with +, try to detect country code and add +
    if (!cleanPhone.startsWith("+")) {
        // Try to detect country code from the beginning of the number
        let detectedCode = null;
        let bestMatch = null;

        // Try different country code lengths (1-4 digits) - start from longest to shortest
        // This ensures we match the most specific country code first
        for (let i = 4; i >= 1; i--) {
            const potentialCode = "+" + cleanPhone.substring(0, i);

            if (countryCodeMap[potentialCode]) {
                // Check if this is a valid match by ensuring the remaining digits make sense
                const remainingDigits = cleanPhone.substring(i);

                // Basic validation: remaining digits should be at least 7 for most countries
                if (remainingDigits.length >= 7) {
                    detectedCode = potentialCode;
                    bestMatch = i;
                    break;
                }
                // Store as fallback if no better match found
                if (!bestMatch) {
                    detectedCode = potentialCode;
                    bestMatch = i;
                }
            }
        }

        // If no country code detected, check if it might be a US number missing the country code
        if (!detectedCode) {
            // Check if it's a 10-digit US number (missing the +1)
            if (cleanPhone.length === 10 && /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(cleanPhone)) {
                cleanPhone = "+1" + cleanPhone;
            }
            // Check if it's an 11-digit number that might be US with leading 1
            else if (
                cleanPhone.length === 11 &&
                cleanPhone.startsWith("1") &&
                /^1[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(cleanPhone)
            ) {
                cleanPhone = "+" + cleanPhone;
            }
            // For other cases, return error with helpful message
            else {
                return {
                    isValid: false,
                    error: "Phone number must start with a valid country code (e.g., +1, +44, +49 or 1, 44, 49). For US numbers, use 10 digits without country code or 11 digits with country code 1.",
                    countryCode: null,
                    detectedCountry: null
                };
            }
        } else {
            // Add the + prefix to normalize the phone number
            cleanPhone = "+" + cleanPhone;
        }
    }

    // Check minimum length (at least +1 and 7 digits)
    if (cleanPhone.length < 8) {
        return {
            isValid: false,
            error: "Phone number is too short",
            countryCode: null,
            detectedCountry: null
        };
    }

    // Extract potential country codes (try 1-4 digits after +)
    let detectedCountryCode = null;
    let detectedCountry = null;

    // Try different country code lengths (1-4 digits) - prioritize longer codes first
    // This ensures we match the most specific country code
    for (let i = 4; i >= 1; i--) {
        const potentialCode = cleanPhone.substring(0, i + 1); // +1, +44, +212, etc.

        if (countryCodeMap[potentialCode]) {
            // Validate that the remaining digits make sense for this country
            const remainingDigits = cleanPhone.substring(potentialCode.length);

            // Basic sanity check - remaining digits should be reasonable length
            if (remainingDigits.length >= 6 && remainingDigits.length <= 12) {
                detectedCountryCode = potentialCode;
                // For +1, distinguish USA vs Canada by area code
                if (potentialCode === "+1" && remainingDigits.length >= 3) {
                    detectedCountry = detectUSorCanada(remainingDigits);
                } else {
                    detectedCountry = countryCodeMap[potentialCode][0];
                }
                break;
            }
            // If no better match, keep this as fallback
            if (!detectedCountryCode) {
                detectedCountryCode = potentialCode;
                if (potentialCode === "+1" && cleanPhone.length > potentialCode.length + 2) {
                    detectedCountry = detectUSorCanada(cleanPhone.substring(potentialCode.length));
                } else {
                    detectedCountry = countryCodeMap[potentialCode][0];
                }
            }
        }
    }

    if (!detectedCountryCode) {
        return {
            isValid: false,
            error: `Unrecognized country code. Phone number: ${cleanPhone}`,
            countryCode: null,
            detectedCountry: null
        };
    }

    // Additional validation for specific country formats
    const remainingDigits = cleanPhone.substring(detectedCountryCode.length);

    // Basic length validation based on country
    if (detectedCountryCode === "+1" && remainingDigits.length !== 10) {
        return {
            isValid: false,
            error: "US/Canada phone numbers should have 10 digits after country code",
            countryCode: detectedCountryCode,
            detectedCountry: detectedCountry
        };
    }

    if (detectedCountryCode === "+44" && (remainingDigits.length < 9 || remainingDigits.length > 10)) {
        return {
            isValid: false,
            error: "UK phone numbers should have 9-10 digits after country code",
            countryCode: detectedCountryCode,
            detectedCountry: detectedCountry
        };
    }

    return {
        isValid: true,
        error: null,
        countryCode: detectedCountryCode,
        detectedCountry: detectedCountry,
        formattedPhone: cleanPhone
    };
};

export const detectCountryFromPhone = (phoneNumber) => {
    const validation = validatePhoneNumber(phoneNumber);
    return validation.detectedCountry;
};

export const formatPhoneNumber = (phoneNumber, countryCode) => {
    if (!phoneNumber) return phoneNumber;

    const validation = validatePhoneNumber(phoneNumber);
    if (validation.isValid) {
        return validation.formattedPhone;
    }

    return phoneNumber;
};

/**
 * VOIP-specific phone utilities
 */

/**
 * Normalize phone number for VOIP API calls
 * @param {string} phoneNumber - Phone number to normalize
 * @returns {string} - Normalized phone number (digits only)
 */
export const normalizeForVoip = (phoneNumber) => {
    if (!phoneNumber) return "";
    return phoneNumber.replace(/\D/g, "");
};

/**
 * Validate phone number for VOIP calling
 * @param {string} phoneNumber - Phone number to validate
 * @returns {Object} - Validation result
 */
export const validateForVoip = (phoneNumber) => {
    if (!phoneNumber) {
        return {
            isValid: false,
            error: "Phone number is required"
        };
    }

    const digits = phoneNumber.replace(/\D/g, "");

    if (digits.length < 7) {
        return {
            isValid: false,
            error: "Phone number is too short for calling"
        };
    }

    if (digits.length > 15) {
        return {
            isValid: false,
            error: "Phone number is too long for calling"
        };
    }

    return {
        isValid: true,
        normalizedNumber: digits
    };
};

/**
 * Check if phone number is likely mobile (basic heuristics)
 * @param {string} phoneNumber - Phone number to check
 * @returns {boolean} - True if likely mobile
 */
export const isMobileNumber = (phoneNumber) => {
    const digits = normalizeForVoip(phoneNumber);

    // US mobile patterns (basic check)
    if (digits.length === 10 || (digits.length === 11 && digits.startsWith("1"))) {
        const areaCode = digits.length === 11 ? digits.slice(1, 4) : digits.slice(0, 3);
        // Common mobile area codes (this is a basic list)
        const mobileAreaCodes = [
            "201",
            "202",
            "203",
            "205",
            "206",
            "207",
            "208",
            "209",
            "210",
            "212",
            "213",
            "214",
            "215",
            "216",
            "217",
            "218",
            "219",
            "224",
            "225",
            "228",
            "229",
            "231",
            "234",
            "239",
            "240",
            "248",
            "251"
        ];
        return mobileAreaCodes.includes(areaCode);
    }

    return false;
};

/**
 * Get call priority based on phone number type and time
 * @param {string} phoneNumber - Phone number
 * @returns {string} - Priority level
 */
export const getCallPriority = (phoneNumber) => {
    const isMobile = isMobileNumber(phoneNumber);
    const currentHour = new Date().getHours();

    // Higher priority for mobile numbers during business hours
    if (isMobile && currentHour >= 9 && currentHour <= 17) {
        return "high";
    }

    // Lower priority outside business hours
    if (currentHour < 8 || currentHour > 19) {
        return "low";
    }

    return "normal";
};
